import { useState, useCallback, useMemo, useEffect } from "react";
import { BwjInput } from "../../../framework/entities/bwj-input";
import { BwjValidityState } from "../../../framework/entities/bwj-validity-state";
import { EventNotificationService, useEventNotification } from "../../../framework/services/event-notification";
import { ImmutableFormValueService, ValidatedFormValueService, IFormValidityCheck } from "../../../framework/services/form-model-service-components";
import { NavigationService } from "../../../framework/services/navigation";
import { contentChanged } from "../../../framework/utils";
import { firstOrDefault, none, where } from "../../../framework/utils/collection";
import { IAssessmentSchedulerFormDataModel, AssessmentSchedulerFormDataModel } from "../entities/assessment-sched-fm";
import { ProjectEvents } from "../events";
import { IAssessmentSchedulerFormModel, IAssessmentSchedulerValueModel, IAssessmentSchedulerModelChange } from "./assessment-scheduler-iface";
import { ITimeBlockListing } from "../../../entities-generated/models/time-block/time-block-listing";
import { IAssessmentBookingRequest } from "../../../entities-generated/models/project/assessment-booking-request";
import { IRescheduleAssessmentRequest } from "../../../entities-generated/models/project/reschedule-assessment-request";
import { HOUR_IN_MILLISECONDS } from "../../../framework/utils/date-time";
import { getAnnotatedCustomerNotes } from "../services/customer-note";

// ##BEGIN:DEVELOPERCODE##

// this is a good place for doing things like defining custom form value service types

// ##END:DEVELOPERCODE##

// %%BEGIN:isRescheduling/immutable/boolean%%
export const isRescheduling = new (class isReschedulingService extends ImmutableFormValueService<IAssessmentSchedulerFormDataModel, boolean> {
    public initialize(data: IAssessmentSchedulerFormDataModel): void {
        this.value = data.isRescheduling;
    }

    public applyToDataModel(data: IAssessmentSchedulerFormDataModel): void {
        data.isRescheduling = this.value;
    }

    public getDefaultValue(): boolean {
        return false;
    }

    public getInputValue(): boolean {
        return this.value;
    }

})(
    'isRescheduling' // id
    ,'' // description
);
// %%END:isRescheduling/immutable/boolean%%

// %%BEGIN:customerFirstName/validated/string/string%%
export const customerFirstName = new (class customerFirstNameService extends ValidatedFormValueService<IAssessmentSchedulerFormDataModel, string, string> {
    public initialize(data: IAssessmentSchedulerFormDataModel): void {
        this.setInputValue(new BwjInput<string>(''));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IAssessmentSchedulerFormDataModel): void {}

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string {
        return this.value.value;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = isRescheduling.getOutputValue()
            ? this.getInputObject(value)
            : this.requiredFieldValidator(value, 'Enter customer\'s first name');
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'customerFirstName' // id
    ,'Customer first name / nickname' // description
);
// %%END:customerFirstName/validated/string/string%%

// %%BEGIN:customerName/validated/string/string%%
export const customerName = new (class customerNameService extends ValidatedFormValueService<IAssessmentSchedulerFormDataModel, string, string> {
    public initialize(data: IAssessmentSchedulerFormDataModel): void {
        this.setInputValue(new BwjInput<string>(''));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IAssessmentSchedulerFormDataModel): void {}

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string {
        return this.value.value;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = isRescheduling.getOutputValue()
            ? this.getInputObject(value)
            : this.requiredFieldValidator(value, 'Enter customer\'s full name');
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'customerName' // id
    ,'Customer full formal name' // description
);
// %%END:customerName/validated/string/string%%

// %%BEGIN:houseNumber/validated/string/string%%
export const houseNumber = new (class houseNumberService extends ValidatedFormValueService<IAssessmentSchedulerFormDataModel, string, string> {
    public initialize(data: IAssessmentSchedulerFormDataModel): void {
        this.setInputValue(new BwjInput<string>(''));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IAssessmentSchedulerFormDataModel): void {}

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string {
        return this.value.value;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = isRescheduling.getOutputValue()
            ? this.getInputObject(value)
            : this.requiredFieldValidator(value, 'Enter house number');
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'houseNumber' // id
    ,'House number' // description
);
// %%END:houseNumber/validated/string/string%%

// %%BEGIN:streetName/validated/string/string%%
export const streetName = new (class streetNameService extends ValidatedFormValueService<IAssessmentSchedulerFormDataModel, string, string> {
    public initialize(data: IAssessmentSchedulerFormDataModel): void {
        // TODO: use the data model to set the initial value managed by this service
        this.setInputValue(new BwjInput<string>(''));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IAssessmentSchedulerFormDataModel): void {}

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string {
        return this.value.value;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = isRescheduling.getOutputValue()
            ? this.getInputObject(value)
            : this.requiredFieldValidator(value, 'Enter street name');
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'streetName' // id
    ,'Street name' // description
);
// %%END:streetName/validated/string/string%%

// %%BEGIN:city/validated/string/string%%
export const city = new (class cityService extends ValidatedFormValueService<IAssessmentSchedulerFormDataModel, string, string> {
    public initialize(data: IAssessmentSchedulerFormDataModel): void {
        this.setInputValue(new BwjInput<string>(''));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IAssessmentSchedulerFormDataModel): void {}

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string {
        return this.value.value;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = isRescheduling.getOutputValue()
            ? this.getInputObject(value)
            : this.requiredFieldValidator(value, 'Enter city');
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'city' // id
    ,'City' // description
);
// %%END:city/validated/string/string%%

// %%BEGIN:notes/validated/string/string%%
export const notes = new (class notesService extends ValidatedFormValueService<IAssessmentSchedulerFormDataModel, string, string> {
    public initialize(data: IAssessmentSchedulerFormDataModel): void {
        this.setInputValue(new BwjInput<string>(''));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IAssessmentSchedulerFormDataModel): void {}

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string {
        return this.value.value;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.getInputObject(value);
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'notes' // id
    ,'Notes' // description
);
// %%END:notes/validated/string/string%%

// %%BEGIN:timeBlocks/validated/[number, number]/[number, number]%%
export const timeBlocks = new (class timeBlocksService extends ValidatedFormValueService<IAssessmentSchedulerFormDataModel, [number, number], [number, number]> {
    constructor(id: string, description: string) {
        super(id, description);
        this.onListUpdate = this.onListUpdate.bind(this);
    }

    public initialize(data: IAssessmentSchedulerFormDataModel): void {
        this.options = data.timeBlocks;
        this.setInputValue(new BwjInput<[number, number]>([0, 0]));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IAssessmentSchedulerFormDataModel): void {
        data.timeBlocks = this.options;
    }

    public getDefaultValue(): BwjInput<[number, number]> {
        return new BwjInput<[number, number]>([0, 0]);
    }

    public getInputValue(): BwjInput<[number, number]> {
        return this.value;
    }

    public setInputValue(value: BwjInput<[number, number]>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): [number, number] {
        return this.value.value;
    }

    public validate(value: [number, number]): Promise<BwjInput<[number, number]>> {
        const result = this.getInputObject(value);
        if(value[0] === 0) {
            result.validity = BwjValidityState.Invalid;
            result.message = 'Block out time to perform the assessment'
        }
        return Promise.resolve(result);
    }

    public onUpdate(value: [number, number]): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }
    public onListUpdate (value: [number, number] | null): void {
        this.onUpdate(value ?? [0, 0]);
    }

    public options: ITimeBlockListing[] = [];
})(
    'timeBlocks' // id
    ,'Scheduled date / time block' // description
);
// %%END:timeBlocks/validated/[number, number]/[number, number]%%

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const toFormModel = (): IAssessmentSchedulerFormModel => {
    return {
       isRescheduling: isRescheduling.getInputValue(),
       customerFirstName: customerFirstName.getInputValue(),
       customerName: customerName.getInputValue(),
       houseNumber: houseNumber.getInputValue(),
       streetName: streetName.getInputValue(),
       city: city.getInputValue(),
       notes: notes.getInputValue(),
       timeBlocks: timeBlocks.getInputValue(),
    };
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const toValueModel = (): IAssessmentSchedulerValueModel => {
    return {
       customerFirstName: customerFirstName.getOutputValue(),
       customerName: customerName.getOutputValue(),
       houseNumber: houseNumber.getOutputValue(),
       streetName: streetName.getOutputValue(),
       city: city.getOutputValue(),
       notes: notes.getOutputValue(),
       timeBlocks: timeBlocks.getOutputValue(),
    };
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const getValidatedInputs = () => {
    return [
        customerFirstName.getInputValue(),
        customerName.getInputValue(),
        houseNumber.getInputValue(),
        streetName.getInputValue(),
        city.getInputValue(),
        notes.getInputValue(),
        timeBlocks.getInputValue(),
    ];
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const isFormValid = () => none(getValidatedInputs(), (i) => i.validity === BwjValidityState.Invalid);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const getFormErrors = () => where(getValidatedInputs(), (i) => i.validity === BwjValidityState.Invalid);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const broadcastChange = (propertyName: string) => {
    const arg: IAssessmentSchedulerModelChange = {
        propertyChanged: propertyName,
        model: toFormModel()
    };

    EventNotificationService.dispatch(ProjectEvents.AssessSchedModelDidUpdate, arg);
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const validateAndUpdateModel = (): Promise<IFormValidityCheck<IAssessmentSchedulerFormModel>> => {
    const promises = [
        customerFirstName.formValidateAndUpdate(),
        customerName.formValidateAndUpdate(),
        houseNumber.formValidateAndUpdate(),
        streetName.formValidateAndUpdate(),
        city.formValidateAndUpdate(),
        notes.formValidateAndUpdate(),
        timeBlocks.formValidateAndUpdate(),
    ];

    return Promise.all(promises)
        .then(() => { 
            broadcastChange('validateAndUpdateModel:IAssessmentSchedulerFormModel');
            return { isValid: isFormValid(), model: toFormModel() };
        });
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const isModelChanged = () => {
    if(initialState.valueModel === undefined) { return false; }
    const current = toValueModel();
    return contentChanged(initialState.valueModel, current);
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const initialState: { valueModel?: IAssessmentSchedulerValueModel, dataModel: IAssessmentSchedulerFormDataModel } = {
    dataModel: new AssessmentSchedulerFormDataModel(false, [])
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const initializeService = (data?: IAssessmentSchedulerFormDataModel) => {
    const dataModel = data || new AssessmentSchedulerFormDataModel(false, []);

    isRescheduling.initialize(dataModel);
    customerFirstName.initialize(dataModel);
    customerName.initialize(dataModel);
    houseNumber.initialize(dataModel);
    streetName.initialize(dataModel);
    city.initialize(dataModel);
    notes.initialize(dataModel);
    timeBlocks.initialize(dataModel);

    initialState.valueModel = toValueModel();
    initialState.dataModel = dataModel;
    broadcastChange('initializeService:IAssessmentSchedulerFormModel');
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
customerFirstName.setChangePublisher(broadcastChange);
customerName.setChangePublisher(broadcastChange);
houseNumber.setChangePublisher(broadcastChange);
streetName.setChangePublisher(broadcastChange);
city.setChangePublisher(broadcastChange);
notes.setChangePublisher(broadcastChange);
timeBlocks.setChangePublisher(broadcastChange);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const setReversionState = () => {
    const dataModel = new AssessmentSchedulerFormDataModel(false, []);

    isRescheduling.applyToDataModel(dataModel);
    customerFirstName.applyToDataModel(dataModel);
    customerName.applyToDataModel(dataModel);
    houseNumber.applyToDataModel(dataModel);
    streetName.applyToDataModel(dataModel);
    city.applyToDataModel(dataModel);
    notes.applyToDataModel(dataModel);
    timeBlocks.applyToDataModel(dataModel);

    initialState.valueModel = toValueModel();
    initialState.dataModel = dataModel;
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const revertChanges = () => {
    initializeService(initialState.dataModel);
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useFormModelChangeEvent = (onChangeWrappedInUseCallback: ((change: IAssessmentSchedulerModelChange) => void), subscriberId: string) =>
    useEventNotification(onChangeWrappedInUseCallback, [onChangeWrappedInUseCallback], ProjectEvents.AssessSchedModelDidUpdate, subscriberId);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useFormModel = (subscriberId: string): IAssessmentSchedulerFormModel => {
    const [getter, setter] = useState<IAssessmentSchedulerFormModel>(toFormModel());

    useFormModelChangeEvent(useCallback((change: IAssessmentSchedulerModelChange) => setter(change.model), []), subscriberId);

    return getter;
}
// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useHasUnsavedChanges = (model: IAssessmentSchedulerFormModel, warnOnUnsavedPageNavigation: boolean = false) => {
    const hasUnsavedChanges = useMemo(isModelChanged, [model]);

    useEffect(() => {
        if(warnOnUnsavedPageNavigation === false || hasUnsavedChanges === false) { return; }

        const unloadCallback = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = '';
            return '';
        };

        window.addEventListener('beforeunload', unloadCallback);
        NavigationService.registerObjection('AssessmentSchedulerFormService');

        return () => {
            window.removeEventListener('beforeunload', unloadCallback);
            NavigationService.rescindObjection('AssessmentSchedulerFormService');
        }
    }, [warnOnUnsavedPageNavigation, hasUnsavedChanges]);

    return hasUnsavedChanges;
}
// ##BEGIN:DEVELOPERCODE##

export const toScheduleRequest = () => {
    const v = toValueModel();
    const tb = firstOrDefault(timeBlocks.options, o => o.blockNumber === v.timeBlocks[0])!;
    const dateScheduled = tb.date + (HOUR_IN_MILLISECONDS * (tb.sequenceNumber < 24 ? tb.sequenceNumber : 23));
    const request: IAssessmentBookingRequest = {
        customerName: v.customerName,
        customerFirstName: v.customerFirstName,
        houseNumber: v.houseNumber,
        streetName: v.streetName,
        city: v.city,
        dateScheduled: dateScheduled,
        startBlockNumber: v.timeBlocks[0],
        endBlockNumber: v.timeBlocks[1],
        customerNotes: getAnnotatedCustomerNotes(v.notes, 'Assessment scheduling')
    };
    return request;
}

export const toRescheduleRequest = () => {
    const v = toValueModel();
    const tb = firstOrDefault(timeBlocks.options, o => o.blockNumber === v.timeBlocks[0])!;
    const dateScheduled = tb.date + (HOUR_IN_MILLISECONDS * (tb.sequenceNumber < 24 ? tb.sequenceNumber : 23));
    const request: IRescheduleAssessmentRequest = {
        dateScheduled: dateScheduled,
        startBlockNumber: v.timeBlocks[0],
        endBlockNumber: v.timeBlocks[1],
        customerNotes: getAnnotatedCustomerNotes(v.notes, 'Assessment rescheduling')
    };
    return request;
}

// ##END:DEVELOPERCODE##
